// İSG uzmanı portalı — Saha bilgileri (api-contract §10.3, tenant-scoped). Firmanın
// saha listesi (UTable sözleşmesi: page/pageSize/search + total/filteredTotal) +
// **şube filtresi** (`?branchUuid=`), oluştur/düzenle (SiteFormModal — şube seçici
// zorunlu), sil (soft-delete, onay). Firmaya kapsanmış; şube üzerinden firmaya bağlı.

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  Pagination,
  RowActionsMenu,
  SelectMenu,
  type SelectOption,
  type SiteSettingsDto,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { SiteFormModal } from '../../components/settings/SiteFormModal';
import { settingsKeys } from '../../lib/settingsKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;
const BRANCH_OPTIONS_PAGE_SIZE = 200;

export function SettingsSitesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [branchFilter, setBranchFilter] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editSite, setEditSite] = useState<SiteSettingsDto | null>(null);
  const [deleteSite, setDeleteSite] = useState<SiteSettingsDto | null>(null);

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function onBranchFilterChange(value: string) {
    setBranchFilter(value);
    setPage(1);
  }

  // Şube filtresi seçenekleri — saha formuyla aynı sorguyu paylaşır (tek istek).
  const branchesQuery = useQuery({
    queryKey: settingsKeys.branchOptions(),
    queryFn: () =>
      settingsApi.listBranches({
        page: 1,
        pageSize: BRANCH_OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60_000,
  });

  const branchOptions: SelectOption[] = useMemo(
    () => (branchesQuery.data?.items ?? []).map((b) => ({ value: b.uuid, label: b.name })),
    [branchesQuery.data],
  );

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    sortBy: 'name',
    sortDir: 'asc' as const,
    branchUuid: branchFilter.length > 0 ? branchFilter : undefined,
  };

  const query = useQuery({
    queryKey: settingsKeys.sites(params),
    queryFn: () => settingsApi.listSites(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => settingsApi.deleteSite(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.sites() });
      setMessage(t('settings.sites.deleted'));
      setDeleteSite(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || branchFilter);
  function clearFilters() {
    setSearch('');
    setBranchFilter('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('settings.sites.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('settings.sites.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('settings.common.search')}
            searchPlaceholder={t('settings.sites.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('settings.sites.filter.branch')}
                  placeholder={t('settings.sites.filter.allBranches')}
                  disabled={branchesQuery.isPending}
                  options={branchOptions}
                  value={branchFilter}
                  onValueChange={onBranchFilterChange}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('settings.sites.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings.sites.col.name')}</TableHead>
                <TableHead>{t('settings.sites.col.code')}</TableHead>
                <TableHead>{t('settings.sites.col.branch')}</TableHead>
                <TableHead>{t('settings.sites.col.address')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('settings.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('settings.common.empty')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={MapPin}
                      title={t('settings.common.noData')}
                      description={t('settings.common.noData.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('settings.sites.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((siteRow) => (
                  <TableRow key={siteRow.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — ayrı bir
                          görünüm rotası yok, "açmak" düzenleme modalını açmakla AYNI. */}
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null);
                          setEditSite(siteRow);
                        }}
                        className="text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {siteRow.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-muted">
                      {siteRow.code ? (
                        <code className="rounded bg-surface-muted px-1.5 py-0.5 text-xs">
                          {siteRow.code}
                        </code>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-muted">{siteRow.branchName ?? '—'}</TableCell>
                    <TableCell className="text-muted">{siteRow.address ?? '—'}</TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('settings.sites.action.openMenu', { name: siteRow.name })}
                        items={[
                          {
                            key: 'delete',
                            label: t('settings.sites.action.delete'),
                            destructive: true,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteSite(siteRow);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('settings.pagination.nav'),
                previous: t('settings.pagination.previous'),
                next: t('settings.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <SiteFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setMessage(t('settings.sites.created'))}
      />
      <SiteFormModal
        open={editSite !== null}
        onOpenChange={(open) => !open && setEditSite(null)}
        site={editSite}
        onSuccess={() => setMessage(t('settings.sites.updated'))}
      />
      <ConfirmDialog
        open={deleteSite !== null}
        onOpenChange={(open) => !open && setDeleteSite(null)}
        title={t('settings.sites.delete.title')}
        body={t('settings.sites.delete.body', { name: deleteSite?.name ?? '' })}
        confirmLabel={t('settings.sites.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteSite && deleteMutation.mutate(deleteSite.uuid)}
      />
    </div>
  );
}
